import { useEffect, useState } from 'react'
import ticketHistoryService from '../../services/ticketHistoryService'

/** Icono por campo modificado. */
const FIELD_ICONS = {
  Creación: 'fa-circle-plus',
  Estado: 'fa-flag',
  Responsable: 'fa-user',
  Cola: 'fa-people-arrows',
  'Asignación automática': 'fa-wand-magic-sparkles',
  Finalización: 'fa-circle-check',
  Prioridad: 'fa-bolt',
  Impacto: 'fa-burst',
  Urgencia: 'fa-hourglass-half',
  Tipo: 'fa-tag',
  Título: 'fa-heading',
  Origen: 'fa-server',
  Descripción: 'fa-align-left',
}

const truncate = (v, n = 60) => {
  const s = String(v ?? '—')
  return s.length > n ? s.slice(0, n) + '…' : s
}

const formatDate = (date) =>
  new Date(date).toLocaleString('es-CR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

/**
 * Línea de tiempo del historial de cambios de un ticket (audit trail).
 *
 * @component
 * @param {Object} props
 * @param {number|string} props.ticketId - Id del ticket.
 * @param {string|number} [props.watch] - Valor que, al cambiar, fuerza recarga.
 * @returns {JSX.Element}
 */
const HistoryTimeline = ({ ticketId, watch }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ticketId) return
    let active = true
    const run = async () => {
      setLoading(true)
      const { success, data } = await ticketHistoryService.getByTicket(ticketId)
      if (!active) return
      setItems(success ? data || [] : [])
      setLoading(false)
    }
    run()
    return () => {
      active = false
    }
  }, [ticketId, watch])

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <h6 className="section-subtitle m-0">Historial de cambios</h6>
        {loading && (
          <span className="spinner-border spinner-border-sm text-primary" />
        )}
      </div>

      {!loading && items.length === 0 ? (
        <p className="text-body-secondary small mb-0">
          Sin cambios registrados todavía.
        </p>
      ) : (
        <ul className="ht-timeline">
          {items.map((h) => (
            <li key={h.id} className="ht-item">
              <span className="ht-dot">
                <i
                  className={`fa-solid ${FIELD_ICONS[h.fieldName] || 'fa-pen'}`}
                />
              </span>
              <div className="ht-body">
                <div className="ht-line">
                  <span className="ht-field">{h.fieldName}</span>
                  <span className="ht-change">
                    {h.oldValue ? (
                      <>
                        <span className="ht-old">{truncate(h.oldValue)}</span>
                        <i className="fa-solid fa-arrow-right-long mx-1 opacity-50" />
                        <span className="ht-new">{truncate(h.newValue)}</span>
                      </>
                    ) : (
                      <span className="ht-new">{truncate(h.newValue)}</span>
                    )}
                  </span>
                </div>
                <div className="ht-meta">
                  {h.changedBy ? `${h.changedBy} · ` : ''}
                  {formatDate(h.createdAt)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <style>{`
        .ht-timeline {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .ht-item {
          position: relative;
          display: flex;
          gap: 12px;
          padding-bottom: 16px;
        }
        .ht-item:not(:last-child)::before {
          content: '';
          position: absolute;
          left: 13px;
          top: 28px;
          bottom: 0;
          width: 2px;
          background: var(--gm-border);
        }
        .ht-dot {
          flex-shrink: 0;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--gm-surface-2);
          border: 1px solid var(--gm-border);
          color: var(--gm-text-muted);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.72rem;
          z-index: 1;
        }
        .ht-body { padding-top: 3px; min-width: 0; }
        .ht-line {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 6px;
          font-size: 0.85rem;
        }
        .ht-field { font-weight: 600; color: var(--gm-text); }
        .ht-change { color: var(--gm-text-secondary, var(--gm-text-muted)); }
        .ht-old { color: var(--gm-text-muted); text-decoration: line-through; }
        .ht-new { color: var(--gm-text); font-weight: 500; }
        .ht-meta {
          font-size: 0.72rem;
          color: var(--gm-text-muted);
          margin-top: 2px;
        }
      `}</style>
    </div>
  )
}

export default HistoryTimeline
